import { useEffect, useState } from "react";
import { Check, Loader2, AlertTriangle, Sparkles, RefreshCw, User, Zap, CheckCircle2 } from "lucide-react";
import { getSubscriptionPrice } from "@/api";

// Dynamic tiered pricing — the actual price is picked from the customer's
// loan_amount by the backend. See utils.get_subscription_price + the
// GET /customer/subscription-price endpoint. Tier boundaries live server-side
// so admins can adjust without a frontend rebuild.
export default function PaymentStep({ loading, onPayment, onSkip }) {
  const [pricing, setPricing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await getSubscriptionPrice();
        if (!cancelled) setPricing(data);
      } catch (e) {
        if (!cancelled) setError("Could not load pricing. Please refresh.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const benefits = [
    { Icon: Sparkles, text: "Expert EMI assistance" },
    { Icon: RefreshCw, text: "Loan restructuring support" },
    { Icon: User, text: "Dedicated relationship manager" },
    { Icon: Zap, text: "Priority processing" },
  ];

  if (!pricing && !error) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 text-red-800 text-sm font-medium" data-testid="payment-price-error">
        {error}
      </div>
    );
  }

  const inr = (n) => (n || 0).toLocaleString("en-IN");

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="bg-gradient-to-br from-blue-50 via-white to-cyan-50 border-2 border-blue-200 rounded-2xl p-6 sm:p-8 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
            <Check className="h-6 w-6 text-white" strokeWidth={3} />
          </div>
          <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>EMI Support Plan</h3>
        </div>

        {/* Tier badge */}
        <div
          data-testid="payment-tier-badge"
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs font-bold px-3 py-1 rounded-full mt-1 tracking-wide shadow"
        >
          {pricing.tier} Plan
        </div>

        {/* Price */}
        <div className="mt-3 flex items-end gap-3 flex-wrap">
          <span data-testid="payment-price" className="text-5xl font-extrabold text-gradient leading-none">
            &#8377;{inr(pricing.price)}
          </span>
          <span className="text-base text-slate-600 font-medium pb-1">one-time</span>
        </div>

        {/* Loan tier context */}
        <p className="mt-2 text-sm text-slate-700">
          Your loan amount <strong>&#8377;{inr(pricing.loan_amount)}</strong> — {pricing.tier_label}
        </p>

        <ul className="mt-6 space-y-3">
          {benefits.map(({ Icon, text }, i) => (
            <li key={i} className="flex items-center gap-3 text-base text-slate-700">
              <div className="w-6 h-6 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                <Icon className="h-3.5 w-3.5 text-green-700" strokeWidth={2.4} />
              </div>
              <span className="font-medium">{text}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* All tiers with active highlight */}
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid="payment-tiers-list">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Pricing Tiers</p>
        <div className="space-y-2">
          {(pricing.tiers || []).map((t) => {
            const active = t.tier === pricing.tier;
            return (
              <div
                key={t.tier}
                data-testid={`payment-tier-row-${t.tier.toLowerCase()}`}
                className={`flex items-center justify-between rounded-lg px-3 py-2 ${
                  active ? "bg-green-100 border border-green-300" : "bg-white border border-slate-200"
                }`}
              >
                <div className="flex items-center gap-2">
                  {active && <CheckCircle2 className="h-4 w-4 text-green-700" strokeWidth={2.5} />}
                  <span className={`text-sm ${active ? "text-green-800 font-semibold" : "text-slate-700"}`}>
                    {t.label}
                  </span>
                </div>
                <span className={`text-sm font-bold ${active ? "text-green-800" : "text-slate-500"}`}>
                  &#8377;{inr(t.price)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
        <p className="text-sm text-amber-900 font-medium">
          The &#8377;{inr(pricing.price)} subscription fee is non-refundable. By proceeding, you acknowledge and accept this policy.
        </p>
      </div>

      <button
        onClick={onPayment}
        data-testid="pay-btn"
        disabled={loading}
        className="btn-gradient w-full h-16 rounded-xl font-bold text-xl shadow-2xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50"
      >
        {loading ? (
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        ) : (
          <span>Pay &#8377;{inr(pricing.price)} &amp; Subscribe</span>
        )}
      </button>

      <button
        type="button"
        onClick={onSkip}
        data-testid="skip-payment-btn"
        className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors"
      >
        Skip to Dashboard
      </button>
    </div>
  );
}
